import React, {useContext, useMemo} from 'react';
import moment from 'moment';
import _isNil from 'lodash/isNil'

import {
  Branding as ApiBranding,
  Carrier,
  Shipment as ApiShipment,
  ShipmentConfirmationRequirementEnum,
  Tracking as ApiTracking,
} from '../apiTypes/api';

export { Carrier, ShipmentConfirmationRequirementEnum } from '../apiTypes/api';

export interface Link {
  url: string;
  text: string;
}

export interface SocialLink {
  type: 'twitter' | 'instagram' | 'youtube' | 'pinterest' | 'facebook' | 'linkedin';
  url: string;
}

interface Toggles {
  show_logo?: boolean;
  show_custom_colors?: boolean;
  show_social_media?: boolean;
  show_store_url?: boolean;
  show_menu_links?: boolean;
  show_return_policy?: boolean;
  show_email?: boolean;
  show_phone?: boolean;
  show_shipment_details?: boolean;
  show_item_images?: boolean;
  show_item_options?: boolean;
  show_shipment_prices?: boolean;
}

export enum TrackingStatusCodes {
  AC = 'AC',
  IT = 'IT',
  DE = 'DE',
  EX = 'EX',
  UN = 'UN',
  AT = 'AT',
  NY = 'NY'
}

enum LayoutKeys {
  Layout01 = 'layout01',
  StationLegacy = 'station_legacy'
}

export enum ThemeKeys {
  Light = 'light',
  Dark = 'dark',
  Peach = 'peach',
  Unicorns = 'unicorns'
}

export interface Currency {
  currency_code?: string | null;
  amount?: string;
}

export interface ShipmentItem {
  image_urls?: {
    medium?: string;
    original: string;
    thumb?: string;
  };
  options?: Array<{ label: string, value: string }>;
  description?: string;
  product_name?: string;
  quantity?: number;
  subtotal_price?: Currency;
  subtotal_cents?: number;
  unit_price?: Currency;
  unit_price_cents?: number;
}

export interface TrackingEvent {
  city_locality?: string;
  country_code?: string;
  description?: string;
  latitude?: number;
  longitude?: number;
  carrier_occurred_at?: string;
  state_province?: string;
}

export interface Branding {
  colors?: {
    primary?: string,
    secondary?: string,
    tertiary?: string
  }
  email?: string;
  layout_key: LayoutKeys;
  logo_type: 'text' | 'image';
  logo_url?: string;
  menu_links: Array<Link>;
  phone?: string;
  powered_by?: {
    name?: string;
    logo_url?: string;
    url: string;
  },
  return_policy?: string;
  social_media: Array<SocialLink>;
  store_name?: string;
  store_url?: string;
  theme_key: ThemeKeys;
  toggles: Toggles;
}

export interface Shipment {
  carrier_code: Carrier;
  confirmation_requirement?: ShipmentConfirmationRequirementEnum;
  currency_code?: string;
  destination: {
    address: string;
    address2?: string;
    city: string;
    country_code?: string;
    postal_code: string;
    state: string;
  };
  external_account_id: string;
  line_items: Array<ShipmentItem>;
  order_number?: string;
  service_name?: string;
  tracking_number: string;
  tracking_url?: string;
}

export interface Tracking {
  estimated_delivery_date?: string;
  timestamp?: string;
  events: Array<TrackingEvent>;
  carrier_status_description?: string;
  isDelivered: boolean;
  latestUpdate: TrackingEvent;
  status_code?: TrackingStatusCodes;
  source?: string;
  sourceEvent: TrackingEvent;
  tracking_number: string;
}


/////////////////////////////////////////////////////////////////////////////
// Shipment Data
/////////////////////////////////////////////////////////////////////////////
const ShipmentDataContext = React.createContext({});
export const ShipmentDataProvider = ShipmentDataContext.Provider;
export function useShipmentData() : Shipment {
  const {
    signature_required,
    ...shipmentData
  } = useContext(ShipmentDataContext) as ApiShipment || {};
  const destination = shipmentData.destination || {};
  const shipmentConfirmation: ShipmentConfirmationRequirementEnum = shipmentData.confirmation_requirement ?
      shipmentData.confirmation_requirement as ShipmentConfirmationRequirementEnum
      : ShipmentConfirmationRequirementEnum.Null;

  const formatSubtotal = (item: ShipmentItem) => {
    if (_isNil(item.subtotal_price))
    {
      const amount = ((item.subtotal_cents || 0)/100).toFixed(2)
      const currency = shipmentData.currency_code || 'USD'
      return { amount: amount, currency_code: currency }
    }
    else
    {
      return item.subtotal_price
    }
  }

  const formatUnitPrice = (item: ShipmentItem) => {
    if (_isNil(item.unit_price)) {
      const amount = ((item.unit_price_cents || 0) / 100).toFixed(2)
      const currency = shipmentData.currency_code || 'USD'

      return { amount: amount, currency_code: currency }
    }
    else {
      return item.unit_price
    }
  }

  return {
    carrier_code: shipmentData.carrier_code,
    confirmation_requirement: shipmentConfirmation,
    currency_code: shipmentData.currency_code || 'USD',
    destination: {
      address: destination.address,
      address2: destination.address2,
      city: destination.city,
      country_code: destination.country_code,
      postal_code: destination.postal_code,
      state: destination.state,
    },
    external_account_id: shipmentData.external_account_id,
    line_items: (shipmentData.line_items || []).map(item => (
        {
          image_urls: item.image_urls ? ({
            medium: item.image_urls.medium,
            original: item.image_urls.original,
            thumb: item.image_urls.thumb
          }) : undefined,
          options: item.options,
          description: item.description,
          product_name: item.product_name,
          quantity: item.quantity,
          subtotal_price: formatSubtotal(item),
          unit_price: formatUnitPrice(item)
        }
    )),
    order_number: shipmentData.order_number,
    service_name: shipmentData.service_name,
    tracking_number: shipmentData.tracking_number,
    tracking_url: shipmentData.tracking_url
  };
}


/////////////////////////////////////////////////////////////////////////////
// Branding Data
/////////////////////////////////////////////////////////////////////////////
const DEFAULT_TOGGLES = {
  show_logo: true,
  show_custom_colors: true,
  show_social_media: true,
  show_store_url: true,
  show_menu_links: true,
  show_return_policy: true,
  show_phone: true,
  show_email: true,
  show_shipment_details: true,
  show_item_images: true,
  show_item_options: true,
  show_shipment_prices: true
};

const BrandingDataContext = React.createContext({});
export const BrandingDataProvider = BrandingDataContext.Provider;
export function useBrandingData(): Branding {
  const brandingData = useContext(BrandingDataContext) as ApiBranding;

  let layout_key = LayoutKeys.Layout01;
  Object.values(LayoutKeys).forEach( key => {
    if( brandingData.layout_key as string === key as string ) {
      layout_key = key as LayoutKeys;
    }
  });

  let theme_key = ThemeKeys.Light;
  Object.values(ThemeKeys).forEach( value => {
    if( brandingData.theme_key === value as string ) {
      theme_key = value;
    }
  });

  const toggles = brandingData.toggles || DEFAULT_TOGGLES;

  return {
    colors: toggles.show_custom_colors ? brandingData.colors : undefined,
    email: brandingData.email,
    layout_key: layout_key,
    logo_type: brandingData.logo_type || 'text',
    logo_url: brandingData.logo_url,
    menu_links: (brandingData.menu_links || []).map( link => link ),
    phone: brandingData.phone,
    powered_by: brandingData.powered_by ? (
      {
        name: brandingData.powered_by.name,
        logo_url: brandingData.powered_by.logo_url,
        url: brandingData.powered_by.url
      }
    ) : undefined,
    return_policy: brandingData.return_policy,
    social_media: (brandingData.social_media || []).map( link => link ),
    store_name: brandingData.store_name,
    store_url: brandingData.store_url,
    theme_key: theme_key,
    toggles: toggles,
  };
}


/////////////////////////////////////////////////////////////////////////////
// Tracking Data
/////////////////////////////////////////////////////////////////////////////
export const useOrderedCarrierEvents = (events: any) => {

  // Note: Assumes that events are already sorted by occurrence but we don't
  // know which direction. Finds the first and last event w/ valid timestamps
  // and compares to keep the order or reverse. This keep any events w/
  // invalid timestamps in place.
  return useMemo( () => {
    let orderedEvents = events || [];

    if( orderedEvents.length > 1 ) {
      const reversedEvents = [...orderedEvents].reverse();
      const firstTimestampedEvent = orderedEvents.find( (e: any) => e.carrier_occurred_at && moment(e.carrier_occurred_at).isValid() );
      const lastTimestampedEvent = reversedEvents.find( (e: any) => e.carrier_occurred_at && moment(e.carrier_occurred_at).isValid() );

      if( moment(firstTimestampedEvent.carrier_occurred_at).isBefore(moment(lastTimestampedEvent.carrier_occurred_at)) ) {
        orderedEvents = reversedEvents;
      }
    }

    return orderedEvents;
  }, [events] );
}

const TrackingDataContext = React.createContext({});
export const TrackingDataProvider = TrackingDataContext.Provider;
export function useTrackingData() : Tracking {
  const trackingData = (useContext(TrackingDataContext) || {}) as ApiTracking;
  const orderedEvents = useOrderedCarrierEvents(trackingData.events);

  let status_code: TrackingStatusCodes | undefined = undefined;
  Object.values(TrackingStatusCodes).forEach( status => {
    if( trackingData.status_code as string === status as string ) {
      status_code = status as TrackingStatusCodes;
    }
  });

  return {
    estimated_delivery_date: trackingData.estimated_delivery_date,
    timestamp: trackingData.timestamp,
    events: orderedEvents,
    carrier_status_description: trackingData.carrier_status_description,
    isDelivered: (trackingData.status_code || '').toUpperCase() === 'DE',
    latestUpdate: (orderedEvents && orderedEvents[0]),
    status_code: status_code,
    source: trackingData.source,
    sourceEvent: (orderedEvents && orderedEvents[orderedEvents.length-1]),
    tracking_number: trackingData.tracking_number
  };
}


/////////////////////////////////////////////////////////////////////////////
// Preview Config
/////////////////////////////////////////////////////////////////////////////
const PreviewConfigContext = React.createContext({ mobileNavOpen: false, isPreview: false });
export const PreviewConfigProvider = PreviewConfigContext.Provider;
export function usePreviewConfig() {
  return useContext(PreviewConfigContext) || {};
}